import { Button } from '@/components/Button';
import useFetchIam from '../../api/useFetchIam';
import type { CandidateMailComposer } from '../hooks/useCandidateMailComposer';
import useCandidateMailNavigation from '../hooks/useCandidateMailNavigation';
import CandidateMailPreviewModal from './CandidateMailPreviewModal';
import CandidateMailSendConfirmDialog from './CandidateMailSendConfirmDialog';
import CandidateMailTemplateSaveModal from './CandidateMailTemplateSaveModal';
import MailDialog from './MailDialog';
import ScheduleDateTimeDialog from './ScheduleDateTimeDialog';

type CandidateMailComposeDialogsProps = {
  composer: CandidateMailComposer;
  onCancelSenderSetting: () => void;
};

export default function CandidateMailComposeDialogs({
  composer,
  onCancelSenderSetting,
}: CandidateMailComposeDialogsProps) {
  const navigation = useCandidateMailNavigation();
  const { data: iam } = useFetchIam();
  const canEditMailSettings = Boolean(iam?.current_user?.is_admin || iam?.current_user?.is_support);

  return (
    <>
      <CandidateMailPreviewModal
        open={composer.isPreviewOpen}
        onClose={composer.closePreview}
        senderName={composer.senderName}
        senderEmail={composer.senderEmail}
        signature={composer.signature}
        recipients={composer.recipients}
        subject={composer.subject}
        body={composer.body}
        saveAsTemplate={composer.saveAsTemplate}
        onSaveAsTemplateChange={composer.setSaveAsTemplate}
        onSendNow={composer.requestSendNow}
        onSchedule={composer.openScheduleDialog}
        isSending={composer.isSending}
      />
      <ScheduleDateTimeDialog
        open={composer.isScheduleDialogOpen}
        title="送信日時を設定"
        onClose={composer.closeScheduleDialog}
        onSubmit={composer.submitSchedule}
      />
      <CandidateMailSendConfirmDialog
        open={composer.sendConfirmation !== null}
        onClose={composer.closeSendConfirmation}
        onConfirm={() => void composer.send()}
        recipientCount={composer.recipients.length}
        subject={composer.subject.trim()}
        scheduledAt={composer.sendConfirmation?.scheduledAt ?? null}
        isSending={composer.isSending}
      />
      <CandidateMailTemplateSaveModal
        open={composer.isTemplateSaveOpen}
        initialName={composer.subject.trim().slice(0, 100)}
        onClose={composer.finishTemplateSave}
        onConfirm={composer.saveTemplate}
        isSaving={composer.isSavingTemplate}
      />
      <MailDialog
        open={composer.pendingTemplateId !== null}
        onClose={composer.cancelTemplateApply}
        title="内容の上書き"
        footer={
          <>
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={composer.cancelTemplateApply}
            >
              キャンセル
            </Button>
            <Button type="button" color="primary" size="md" onClick={composer.confirmTemplateApply}>
              適用
            </Button>
          </>
        }
      >
        <p className="text-body-sm text-main">
          テンプレートを適用すると、現在の件名と本文は上書きされます。テンプレートを適用してよろしいですか？
        </p>
      </MailDialog>
      <MailDialog
        open={composer.isSenderSettingRequired}
        onClose={onCancelSenderSetting}
        title="メール設定が未設定です"
        footer={
          <>
            <Button type="button" color="secondary" size="md" onClick={onCancelSenderSetting}>
              閉じる
            </Button>
            {canEditMailSettings && (
              <Button
                type="button"
                color="primary"
                size="md"
                onClick={() => void navigation.openFolder('settings')}
              >
                メール基本設定へ移動する
              </Button>
            )}
          </>
        }
      >
        <p className="text-body-sm text-main">
          {canEditMailSettings
            ? 'メールを作成・送信するにはメール設定を完了する必要があります。メール設定の画面で送信者名と返信先を設定してください。'
            : 'メールを作成・送信するにはメール設定を完了する必要があります。管理者に送信者名と返信先の設定を依頼してください。'}
        </p>
      </MailDialog>
    </>
  );
}
